import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { useState } from "react"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  cancelVirtualTournament,
  getVirtualTournamentCards,
  getVirtualTournamentCurrent,
  registerVirtualTournament,
} from "@/api/virtualTournamentApi"
import { ColorRank } from "@/types/battle"
import { RANK_NAMES } from "@/constants/guardians"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"

const RANKS: ColorRank[] = ["WHITE", "GREEN", "BLUE", "PURPLE", "ORANGE", "RED"]

export default function VirtualTournamentPage() {
  const queryClient = useQueryClient()
  const [rank, setRank] = useState<ColorRank>("WHITE")
  const [cardId, setCardId] = useState("")

  const currentQuery = useQuery({
    queryKey: ["vt-current"],
    queryFn: getVirtualTournamentCurrent,
  })

  const cardsQuery = useQuery({
    queryKey: ["vt-cards", rank],
    queryFn: () => getVirtualTournamentCards(rank),
  })

  const register = useMutation({
    mutationFn: () => registerVirtualTournament({ colorRank: rank, cardId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["vt-current"] })
    },
  })

  const cancel = useMutation({
    mutationFn: () => cancelVirtualTournament({ colorRank: rank }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["vt-current"] })
    },
  })

  const current = currentQuery.data

  return (
    <GameScreenLayout title="Виртуальный турнир" backTo="/play">
      {currentQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : current ? (
        <div className="space-y-4 text-sm">
          <p className="text-guardians-muted">
            Неделя {current.weekStart} · регистрация{" "}
            {current.registrationOpen ? "открыта" : "закрыта"}
          </p>
          <ul className="space-y-2">
            {current.ranks.map((r) => (
              <li
                key={r.colorRank}
                className="flex items-center justify-between gap-2 rounded-lg border border-white/10 px-3 py-2"
              >
                <span>{RANK_NAMES[r.colorRank]}</span>
                <span className="text-guardians-muted">
                  {r.participantCount}/{r.maxParticipants} · {r.status}
                </span>
                {r.tournamentId ? (
                  <Link
                    to={`/play/virtual-tournament/${encodeURIComponent(r.tournamentId)}?colorRank=${r.colorRank}`}
                    className="shrink-0 text-xs text-guardians-accent underline"
                  >
                    Сетка
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-guardians-muted">Турнир не объявлен</p>
      )}

      <section className="mt-6 space-y-2">
        <h3 className="text-sm font-medium">Регистрация</h3>
        <select
          value={rank}
          onChange={(e) => setRank(e.target.value as ColorRank)}
          className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
        >
          {RANKS.map((r) => (
            <option key={r} value={r}>
              {RANK_NAMES[r]}
            </option>
          ))}
        </select>
        <select
          value={cardId}
          onChange={(e) => setCardId(e.target.value)}
          className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
        >
          <option value="">Карта</option>
          {(cardsQuery.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.element} Lv.{c.level}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!cardId || register.isPending}
            onClick={() => register.mutate()}
            className="flex-1 rounded-xl bg-guardians-accent py-2 text-guardians-bg disabled:opacity-40"
          >
            Записаться
          </button>
          <button
            type="button"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
            className="rounded-xl border border-white/20 px-4 py-2"
          >
            Отмена
          </button>
        </div>
      </section>

      {(register.isError || cancel.isError) && (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(register.error ?? cancel.error, "Ошибка турнира")}
        </p>
      )}
    </GameScreenLayout>
  )
}
